import { useEffect } from 'react';

const SKIP_WHEEL_ROOT_SELECTORS = [
  '.modal-overlay',
  '.reusable-dropdown-menu--portal',
  '.cnr-emoji-popover--floating',
  '.wfm-text-editor--mobile-expanded',
  '.tooltip-portal',
].join(',');

const getScrollableWithin = (target, boundary) => {
  let node = target instanceof Node ? target : null;
  while (node && node !== boundary) {
    if (node instanceof HTMLElement) {
      const { overflowY } = window.getComputedStyle(node);
      if (
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight
      ) {
        return node;
      }
    }
    node = node.parentElement;
  }
  return null;
};

const canScrollElement = (el, deltaY) => {
  if (!el || el.scrollHeight <= el.clientHeight) return false;
  if (deltaY < 0) return el.scrollTop > 0;
  if (deltaY > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
  return false;
};

const isScrollPane = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  const { overflowY } = window.getComputedStyle(el);
  return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
};

const pointInRect = (x, y, rect) =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/**
 * Routes wheel events to the left nav, main feed, or right aside scrollport based on pointer position.
 */
export function usePostsShellWheelScroll(shellRef, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const handleWheel = (event) => {
      if (!window.matchMedia('(min-width: 961px)').matches) return;

      const root = shellRef.current;
      if (!root) return;

      if (event.target instanceof Element && event.target.closest(SKIP_WHEEL_ROOT_SELECTORS)) {
        return;
      }

      const { clientX, clientY, deltaY } = event;
      if (!deltaY) return;

      const leftCol = root.querySelector('.support-open-disc-shell-sidebar-col');
      const leftPane = root.querySelector('.support-open-disc-shell-sidebar-col-inner');
      const mainPane =
        root.querySelector('.support-open-disc-home-main') ||
        root.querySelector('.posts-page-layout__center');
      const rightPane = root.querySelector('.support-open-disc-feed-aside');

      let pane = mainPane;
      if (leftCol) {
        const leftRect = leftCol.getBoundingClientRect();
        if (pointInRect(clientX, clientY, leftRect)) {
          pane = leftPane;
        }
      }
      if (rightPane) {
        const rightRect = rightPane.getBoundingClientRect();
        if (pointInRect(clientX, clientY, rightRect)) {
          pane = rightPane;
        }
      }

      if (!(pane instanceof HTMLElement) || !isScrollPane(pane)) return;

      const nestedScrollable = getScrollableWithin(event.target, pane);
      if (nestedScrollable && nestedScrollable !== pane && canScrollElement(nestedScrollable, deltaY)) {
        return;
      }

      if (canScrollElement(pane, deltaY)) {
        pane.scrollTop += deltaY;
        event.preventDefault();
        return;
      }

      // Block scroll chaining to the document when the pane is at an edge.
      if (pane.contains(event.target) || pane === mainPane) {
        event.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', handleWheel, { capture: true });
  }, [shellRef, enabled]);
}
